import { num } from '../format';

/**
 * Certificates inside the course: progress toward each, the offer to a free
 * learner, and the moment one is issued (docs/certificates.md). Names and the
 * claim come from ./certificate.
 *
 * Two rules, both from elsewhere and both binding here.
 *
 * **No server in any sentence.** The product rule in ./checkpoint.ts holds on
 * this side of the course too: "Check my work" is one button, and a sentence
 * about where a checkpoint was graded turns it back into two. What a
 * certificate claims, and where the grading happened, is said once — on its
 * public page, to the reader who is checking it (./certificatePage.ts). The
 * course says what the learner did and what it earned.
 *
 * **No pressure.** The free learner is told how far they are and that
 * certificates come with the course, and nothing else: no countdown, no "only",
 * no loss framed as a deadline. The passes are kept whatever they decide
 * (docs/certificates.md, "Who gets one"), and the copy says so rather than
 * letting the reader fear the opposite.
 */

/** "4 checkpoints", "one checkpoint": the counted phrase the sentences below are built from. */
const checkpointsEn = (n: number) => (n === 1 ? 'one checkpoint' : `${num(n, 'en')} checkpoints`);

export const en = {
  /* The strip on the map (components/home/Certificates.tsx). */
  strip: {
    label: 'Certificates',
    /** Under the label for an account that owns the course. */
    noteOwner: 'Each is issued the moment its last checkpoint passes.',
    /** The same place, for one that does not. The second sentence is the promise the contract makes. */
    noteFree: 'Included with the course. Checkpoints you pass now count the moment it is yours.',
    toGo: (left: number) => (left === 1 ? 'One checkpoint to go' : `${num(left, 'en')} checkpoints to go`),
    /** Every checkpoint passed on an account that does not own the course. */
    allPassedFree: 'All passed. Issued with the course.',
    issued: (date: string) => `Issued ${date}`,
    /**
     * A certificate the course was taken back from. The row stays and the
     * link still opens: its public page says it is no longer valid, which is
     * what somebody holding an old link must be told (docs/certificates.md).
     */
    revoked: 'No longer valid',
    /** The accessible name of an issued card's link; the id itself is the visible text. */
    view: (name: string, id: string) => `View certificate: ${name}, ${id}`,
    meterLabel: (name: string, passed: number, total: number) =>
      `${name}: ${num(passed, 'en')} of ${num(total, 'en')} checkpoints passed`,
  },

  /* In the offer block, for a free account with a pass (components/home/Offer.tsx). */
  offer: (passed: number, total: number, name: string) =>
    passed >= total
      ? `You have passed all ${num(total, 'en')} checkpoints for ${name}. Certificates are included with the course, and this one is issued the moment it is yours.`
      : `You have passed ${num(passed, 'en')} of the ${num(total, 'en')} checkpoints for ${name}. Certificates are included with the course.`,

  /* On the clear overlay, after a checkpoint passes (components/play/ClearOverlay.tsx). */
  clear: {
    issuedLabel: 'Certificate issued',
    /** Why the link is worth having, without naming what stands behind the page. */
    issuedLine: 'It has a public page. Anyone you send the link to can check it.',
    view: 'View certificate',
    /** This pass moved a certificate on and did not finish it. */
    toward: (passed: number, total: number, name: string) =>
      `That is ${num(passed, 'en')} of the ${num(total, 'en')} checkpoints for ${name}.`,
    /** The same, for an account that does not own the course. */
    towardFree: (passed: number, total: number, name: string) =>
      `That is ${num(passed, 'en')} of the ${num(total, 'en')} checkpoints for ${name}. Certificates are included with the course.`,
    /** The last checkpoint of a certificate, on an account that does not own the course. */
    completeFree: (total: number, name: string) =>
      `That completes the ${checkpointsEn(total)} for ${name}. Certificates are included with the course, and this one is issued the moment it is yours.`,
  },
};

/**
 * `محطة واحدة`, `محطتان`, `5 محطات`, `15 محطة`: the counted noun for the one
 * phrase that stands alone. Everywhere else the count sits after `من` with the
 * total, where `من 6 محطات` needs only the few/other split.
 */
const checkpointsAr = (n: number) =>
  n === 1 ? 'محطة واحدة' : n === 2 ? 'محطتان' : n % 100 >= 3 && n % 100 <= 10 ? `${num(n, 'ar')} محطات` : `${num(n, 'ar')} محطة`;
const ofTotalAr = (total: number) =>
  total === 2 ? 'محطتين' : total % 100 >= 3 && total % 100 <= 10 ? `${num(total, 'ar')} محطات` : `${num(total, 'ar')} محطة`;

export const ar: typeof en = {
  strip: {
    label: 'الشهادات',
    noteOwner: 'تصدر كل شهادة لحظة اجتياز آخر محطة فيها.',
    noteFree: 'الشهادات مشمولة في الدورة. المحطات التي تجتازها الآن تُحتسب لحظة امتلاكك للدورة.',
    toGo: (left: number) => (left === 1 ? 'بقيت محطة واحدة' : `بقي ${checkpointsAr(left)}`),
    allPassedFree: 'اجتزتها كلها. تصدر مع الدورة.',
    issued: (date: string) => `صدرت في ${date}`,
    revoked: 'لم تعد سارية',
    view: (name: string, id: string) => `اعرض الشهادة: ${name}، ${id}`,
    meterLabel: (name: string, passed: number, total: number) =>
      `${name}: اجتزت ${num(passed, 'ar')} من ${ofTotalAr(total)}`,
  },

  offer: (passed: number, total: number, name: string) =>
    passed >= total
      ? `اجتزت كل المحطات الـ${num(total, 'ar')} لشهادة «${name}». الشهادات مشمولة في الدورة، وتصدر هذه لحظة امتلاكك لها.`
      : `اجتزت ${num(passed, 'ar')} من ${ofTotalAr(total)} لشهادة «${name}». الشهادات مشمولة في الدورة.`,

  clear: {
    issuedLabel: 'صدرت شهادة',
    issuedLine: 'لها صفحة عامة، ويستطيع كل من ترسل إليه الرابط أن يتحقق منها.',
    view: 'اعرض الشهادة',
    toward: (passed: number, total: number, name: string) =>
      `بهذا اجتزت ${num(passed, 'ar')} من ${ofTotalAr(total)} لشهادة «${name}».`,
    towardFree: (passed: number, total: number, name: string) =>
      `بهذا اجتزت ${num(passed, 'ar')} من ${ofTotalAr(total)} لشهادة «${name}». الشهادات مشمولة في الدورة.`,
    completeFree: (total: number, name: string) =>
      `بهذا أكملت ${checkpointsAr(total)} لشهادة «${name}». الشهادات مشمولة في الدورة، وتصدر هذه لحظة امتلاكك لها.`,
  },
};

const checkpointsDe = (n: number) => (n === 1 ? 'einen Checkpoint' : `${num(n, 'de')} Checkpoints`);

export const de: typeof en = {
  strip: {
    label: 'Zertifikate',
    noteOwner: 'Jedes wird in dem Moment ausgestellt, in dem sein letzter Checkpoint besteht.',
    noteFree: 'Im Kurs enthalten. Checkpoints, die du jetzt bestehst, zählen, sobald er dir gehört.',
    toGo: (left) => (left === 1 ? 'Noch ein Checkpoint' : `Noch ${num(left, 'de')} Checkpoints`),
    allPassedFree: 'Alle bestanden. Wird mit dem Kurs ausgestellt.',
    issued: (date) => `Ausgestellt am ${date}`,
    revoked: 'Nicht mehr gültig',
    view: (name, id) => `Zertifikat ansehen: ${name}, ${id}`,
    meterLabel: (name, passed, total) => `${name}: ${num(passed, 'de')} von ${num(total, 'de')} Checkpoints bestanden`,
  },
  offer: (passed, total, name) =>
    passed >= total
      ? `Du hast alle ${num(total, 'de')} Checkpoints für ${name} bestanden. Zertifikate sind im Kurs enthalten, und dieses wird ausgestellt, sobald er dir gehört.`
      : `Du hast ${num(passed, 'de')} von ${num(total, 'de')} Checkpoints für ${name} bestanden. Zertifikate sind im Kurs enthalten.`,
  clear: {
    issuedLabel: 'Zertifikat ausgestellt',
    issuedLine: 'Es hat eine öffentliche Seite. Jeder, dem du den Link schickst, kann es prüfen.',
    view: 'Zertifikat ansehen',
    toward: (passed, total, name) => `Das sind ${num(passed, 'de')} von ${num(total, 'de')} Checkpoints für ${name}.`,
    towardFree: (passed, total, name) =>
      `Das sind ${num(passed, 'de')} von ${num(total, 'de')} Checkpoints für ${name}. Zertifikate sind im Kurs enthalten.`,
    completeFree: (total, name) =>
      `Damit hast du ${checkpointsDe(total)} für ${name} vollständig. Zertifikate sind im Kurs enthalten, und dieses wird ausgestellt, sobald er dir gehört.`,
  },
};

export const ja: typeof en = {
  strip: {
    label: '修了証',
    noteOwner: '最後のチェックポイントに合格した時点で、それぞれ発行されます。',
    noteFree: 'コースに含まれます。今合格したチェックポイントは、コースを開始した時点でカウントされます。',
    toGo: (left: number) => (left === 1 ? 'あと1つのチェックポイント' : `あと${num(left, 'ja')}個のチェックポイント`),
    allPassedFree: 'すべて合格しました。コースの開始時に発行されます。',
    issued: (date: string) => `${date}に発行`,
    revoked: '無効になりました',
    view: (name: string, id: string) => `修了証を見る: ${name}、${id}`,
    meterLabel: (name: string, passed: number, total: number) =>
      `${name}: ${num(total, 'ja')}個中${num(passed, 'ja')}個のチェックポイントに合格`,
  },
  offer: (passed: number, total: number, name: string) =>
    passed >= total
      ? `${name}のチェックポイント${num(total, 'ja')}個すべてに合格しました。修了証はコースに含まれており、この修了証はコースを開始した時点で発行されます。`
      : `${name}のチェックポイント${num(total, 'ja')}個のうち${num(passed, 'ja')}個に合格しました。修了証はコースに含まれています。`,
  clear: {
    issuedLabel: '修了証を発行しました',
    issuedLine: '公開ページがあります。リンクを送った相手なら誰でも、その内容を確認できます。',
    view: '修了証を見る',
    toward: (passed: number, total: number, name: string) =>
      `${name}のチェックポイント${num(total, 'ja')}個のうち、${num(passed, 'ja')}個に合格しています。`,
    towardFree: (passed: number, total: number, name: string) =>
      `${name}のチェックポイント${num(total, 'ja')}個のうち、${num(passed, 'ja')}個に合格しています。修了証はコースに含まれています。`,
    completeFree: (total: number, name: string) =>
      `これで${name}のチェックポイント${num(total, 'ja')}個がそろいました。修了証はコースに含まれており、この修了証はコースを開始した時点で発行されます。`,
  },
};
